import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ClipboardList, Search } from 'lucide-react';
import { getCompanyAuditLogs } from '@/lib/firebase';
import { useAuth } from '@/contexts/auth-context';
import { logger } from '@/lib/logger';

interface AuditChange {
  field: string;
  from: string;
  to: string;
}

interface AuditLog {
  id: string;
  companyId: string;
  companyName: string;
  action: string;
  performedBy: string;
  performedByUid?: string;
  timestamp: any;
  changes?: AuditChange[];
}

const formatDateTime = (ts: any): string => {
  if (!ts) return '—';
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return '—'; }
};

const ACTION_STYLES: Record<string, string> = {
  created: 'bg-green-100 text-green-800 border-green-200',
  trial_created: 'bg-teal-100 text-teal-800 border-teal-200',
  updated: 'bg-blue-100 text-blue-800 border-blue-200',
  trial_updated: 'bg-sky-100 text-sky-800 border-sky-200',
  trial_cancelled: 'bg-red-100 text-red-800 border-red-200',
  converted_to_live: 'bg-purple-100 text-purple-800 border-purple-200',
  deleted: 'bg-rose-100 text-rose-800 border-rose-200',
};

const ACTION_LABELS: Record<string, string> = {
  created: 'Created',
  trial_created: 'Trial Created',
  updated: 'Updated',
  trial_updated: 'Trial Updated',
  trial_cancelled: 'Trial Cancelled',
  converted_to_live: 'Converted to Live',
  deleted: 'Deleted',
};

export default function CompanyAudit() {
  const { user } = useAuth();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!user) return;
    const loadData = async () => {
      try {
        const data = await getCompanyAuditLogs();
        setLogs(data as AuditLog[]);
      } catch (error: any) {
        logger.error('Error loading audit logs:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
    const timer = setInterval(loadData, 15_000);
    return () => clearInterval(timer);
  }, [user]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredLogs(logs);
    } else {
      setFilteredLogs(logs.filter(l =>
        l.companyName?.toLowerCase().includes(searchTerm.toLowerCase())
      ));
    }
  }, [logs, searchTerm]);

  if (loading) {
    return (
      <div className="flex-1 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-center text-slate-600">Loading audit logs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ClipboardList className="h-6 w-6" />
            Company Audit Log
          </h1>
          <p className="text-slate-600 mt-1">
            Track all changes made to customer and trial company records.
          </p>
        </div>

        <Card>
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
              <Input
                placeholder="Search by company name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        <div className="text-sm text-slate-600">
          {filteredLogs.length === 0
            ? `0 of ${logs.length} entries`
            : `Showing ${filteredLogs.length} of ${logs.length} entr${logs.length !== 1 ? 'ies' : 'y'}`}
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Audit Entries</CardTitle>
            <CardDescription>
              All create, update, and delete operations on company records
            </CardDescription>
          </CardHeader>
          <CardContent>
            {filteredLogs.length === 0 ? (
              <div className="text-center py-12">
                <ClipboardList className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">
                  {logs.length === 0 ? 'No audit entries yet' : 'No matching entries'}
                </h3>
                <p className="text-slate-600">
                  {logs.length === 0
                    ? 'Audit entries will appear here when company records are created or modified.'
                    : 'Try adjusting your search term.'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-44">Date / Time</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead className="w-40">Action</TableHead>
                      <TableHead>Performed By</TableHead>
                      <TableHead>Changes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLogs.map((log) => (
                      <TableRow key={log.id} className="align-top">
                        <TableCell className="text-sm text-slate-600 whitespace-nowrap">
                          {formatDateTime(log.timestamp)}
                        </TableCell>
                        <TableCell className="font-medium">{log.companyName || '—'}</TableCell>
                        <TableCell>
                          <Badge className={ACTION_STYLES[log.action] ?? 'bg-slate-100 text-slate-800 border-slate-200'}>
                            {ACTION_LABELS[log.action] ?? log.action}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-slate-700">{log.performedBy || '—'}</TableCell>
                        <TableCell>
                          {log.changes && log.changes.length > 0 ? (
                            <div className="space-y-1">
                              {log.changes.map((change, i) => (
                                <div key={i} className="text-xs leading-relaxed">
                                  <span className="font-medium text-slate-700">{change.field}:</span>{' '}
                                  {change.from ? (
                                    <>
                                      <span className="text-slate-400 line-through">{change.from}</span>
                                      <span className="text-slate-400 mx-1">→</span>
                                    </>
                                  ) : null}
                                  <span className="text-slate-800">{change.to || '—'}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-slate-400 text-sm">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
